// This file configures the initialization of Sentry on the client.
// The added config here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import type { ApiException } from '@repo/api-gw-sdk';
import {
  init,
  replayIntegration,
  captureRouterTransitionStart,
} from '@sentry/nextjs';

declare global {
  interface Window {
    __RUNTIME_CONFIG__: {
      SENTRY_DSN: string;
      ENVIRONMENT: string;
    };
  }
}

init({
  enabled: process.env.NODE_ENV === 'production',
  dsn: window?.__RUNTIME_CONFIG__?.SENTRY_DSN,
  environment: (window?.__RUNTIME_CONFIG__?.ENVIRONMENT || '').toLowerCase(),

  // Adjust this value in production, or use tracesSampler for greater control
  tracesSampleRate: 1,

  replaysOnErrorSampleRate: 1.0,

  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  replaysSessionSampleRate: 0.0,

  // You can remove this option if you're not planning to use the Sentry Session Replay feature:
  integrations: [
    replayIntegration({
      blockAllMedia: false,
      maskAllInputs: false,
    }),
  ],

  beforeSend(errorEvent, hint) {
    const originalException = hint.originalException as ApiException<unknown>;
    if (originalException) {
      const code = originalException.code;
      const body = originalException.body as { error: string };

      if (
        code === 404 ||
        (code === 401 && body?.error?.includes('token is expired')) ||
        (originalException instanceof DOMException &&
          originalException.name === 'AbortError')
      ) {
        return null;
      }
    }

    errorEvent.extra = errorEvent.extra || {};
    errorEvent.extra.fullUrl = window.location.href;

    return errorEvent;
  },
});

export const onRouterTransitionStart = captureRouterTransitionStart;

window.addEventListener('unhandledrejection', (event) => {
  if (!/Loading chunk [\s\S]+ failed/.test(event?.reason?.message)) {
    return;
  }
  const lastLoad = localStorage.getItem('reload');
  if (lastLoad && Date.now() - Number(lastLoad) < 10000) {
    return; // Reloading didn't fix it
  }
  localStorage.setItem('reload', String(Date.now()));
  // Force reload to get new chunks
  window.location.reload();
});
